import { getAppConfigValue } from "./appConfig"

export function getBaseDomain(): string {
    if (typeof window === "undefined") {
        return "localhost"
    }

    const host = window.location.hostname
    const parts = host.split(".")
    
    // IP addresses or localhost
    if (/^\d+\.\d+\.\d+\.\d+$/.test(host) || host === "localhost") {
        return "localhost"
    }

    // If it's tenant.localhost, base is localhost
    if (parts.length >= 2 && parts[parts.length - 1] === "localhost") {
        return "localhost"
    }

    // Standard domain: apple.dbgone.com -> dbgone.com
    // We assume the main domain is the last two parts
    if (parts.length >= 2) {
        return parts.slice(-2).join(".")
    }

    return host
}

export function getTenantSubdomain(): string | null {
    if (typeof window === "undefined") {
        return "selfhosted"
    }

    const host = window.location.hostname
    const parts = host.split(".")
    
    // IP addresses are always treated as selfhosted (no subdomain support)
    if (/^\d+\.\d+\.\d+\.\d+$/.test(host)) {
        return "selfhosted"
    }

    // Localhost handling
    if (host === "localhost") {
        // Pure localhost -> discovery needed
        return null
    }

    // tenant.localhost
    if (parts.length === 2 && parts[1] === "localhost") {
        return parts[0]
    }

    // Main SaaS domain (e.g. dbgone.com)
    if (parts.length === 2) {
        return null
    }

    // Subdomain on SaaS domain (e.g. apple.dbgone.com)
    if (parts.length >= 3) {
        return parts[0]
    }

    return "selfhosted"
}

function trimTrailingSlash(value: string): string {
    return value.replace(/\/+$/, "")
}

function ensureApiPrefix(value: string): string {
    const trimmed = trimTrailingSlash(value)
    return trimmed.endsWith("/api") ? trimmed : `${trimmed}/api`
}

function getRuntimeOrigin(): string {
    if (typeof window === "undefined") {
        return ""
    }

    return window.location.origin
}

function isLocalDevHost(hostname: string): boolean {
    const host = hostname.toLowerCase()
    return host === "localhost" || host.endsWith(".localhost") || /^\d+\.\d+\.\d+\.\d+$/.test(host)
}

export function getApiUrl(): string {
    const origin = getRuntimeOrigin()
    if (typeof window !== "undefined" && isLocalDevHost(window.location.hostname)) {
        return origin ? `${origin}/api` : "/api"
    }

    const configured = getAppConfigValue("VITE_API_DOMAIN")
    if (configured) {
        return ensureApiPrefix(configured)
    }

    return origin ? `${origin}/api` : "/api"
}

export function getWsUrl(path: string, params: Record<string, string> = {}): string {
    let base = ""

    if (typeof window !== "undefined" && isLocalDevHost(window.location.hostname)) {
        const protocol = window.location.protocol === "https:" ? "wss:" : "ws:"
        base = `${protocol}//${window.location.host}`
    } else {
        const configured = getAppConfigValue("VITE_WS_URL")
        if (configured) {
            base = trimTrailingSlash(configured)
        } else if (typeof window !== "undefined") {
            const protocol = window.location.protocol === "https:" ? "wss:" : "ws:"
            base = `${protocol}//${window.location.host}`
        }
    }

    const query = new URLSearchParams(params).toString()
    const suffix = query ? `?${query}` : ""
    return `${base}/ws/${path}${suffix}`
}
